import './register.css';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Form,
  Row,
  Col,
  Button,
  ButtonToolbar,
  Card,
} from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { ServiceFactory } from '../../Api/Services/ServiceFactory';
import appConfig from '../../config/config';
import { useToast } from '../../components/ToastContext/ToastContext';
import { ServiceNames } from '../../Api/registry/Services';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    nome: '',
    cognome: '',
    profilo: 'expert',
    descrizioneProfilo: '',
    termsAccepted: false,
    privacyAccepted: false,
    captchaToken: '',
  });

  const [errors, setErrors] = useState({});
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(0);
  const { t, i18n } = useTranslation(['registerpage']);
  const { showToast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [focusPassword, setFocusPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: {
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      special: false,
    },
  });
  const nameInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setCaptchaKey((prevKey) => prevKey + 1);
  }, [i18n.language]);

  useEffect(() => {
    if (nameInputRef.current) nameInputRef.current.focus();
  }, []);

  const togglePasswordVisibility = (field) => {
    if (field === 'newPassword') setShowNewPassword((prev) => !prev);
    else setShowPassword((prev) => !prev);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => {
      const updatedForm = { ...prev, [name]: updatedValue };

      // Aggiorna forza password
      if (name === 'password') evaluatePassword(updatedValue);

      // Controllo live corrispondenza password
      if (
        (name === 'password' && updatedForm.confirmPassword) ||
        (name === 'confirmPassword' && updatedForm.password)
      ) {
        if (updatedForm.password !== updatedForm.confirmPassword) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: t('registerpage:messages.passwordnotmatch'),
          }));
        } else {
          setErrors((prev) => {
            const newErr = { ...prev };
            delete newErr.confirmPassword;
            return newErr;
          });
        }
      }

      return updatedForm;
    });
  };

  const evaluatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&.]/.test(password),
    };
    const score = Object.values(checks).filter(Boolean).length;
    setPasswordStrength({ score, checks });
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.username)
      newErrors.username = t('registerpage:messages.usernamerequired');
    else if (formData.username.length < 8)
      newErrors.username = t('registerpage:messages.usernamelength');
    else if (formData.username.includes(','))
      newErrors.username = t('registerpage:messages.usernameinvalid');

    const passwordRegex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&.])[A-Za-z\d@$!%*?&.]{8,}$/;

    if (!formData.password)
      newErrors.password = t('registerpage:messages.passwordrequired');
    else if (!passwordRegex.test(formData.password))
      newErrors.password = t('registerpage:messages.passwordpolicymessage');

    if (formData.password !== formData.confirmPassword)
      newErrors.confirmPassword = t('registerpage:messages.passwordnotmatch');
    if (!formData.email.includes('@'))
      newErrors.email = t('registerpage:messages.emailinvalid');
    if (!formData.nome) newErrors.nome = t('registerpage:messages.namerequired');
    if (!formData.cognome)
      newErrors.cognome = t('registerpage:messages.surnamerequired');
    if (!formData.profilo)
      newErrors.profilo = t('registerpage:messages.profilerequired');
    if (!formData.termsAccepted)
      newErrors.termsAccepted = t('registerpage:messages.acceptterms');
    if (!formData.privacyAccepted)
      newErrors.privacyAccepted = t('registerpage:messages.acceptprivacy');
    if (!captchaVerified) newErrors.captcha = t('registerpage:messages.recaptcha');

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) handleRegister();
  };

  const handleCaptchaChange = (value) => {
    if (value) {
      setFormData((prev) => ({ ...prev, captchaToken: value }));
      setCaptchaVerified(true);
    } else setCaptchaVerified(false);
  };

  const handleRegister = async () => {
    const request = {
      name: formData.nome,
      surname: formData.cognome,
      email: formData.email,
      userName: formData.username,
      password: formData.password,
      profile: formData.profilo,
      captchaToken: formData.captchaToken,
      termsAccepted: formData.termsAccepted,
      privacyAccepted: formData.privacyAccepted,
    };

    try {
      const userService = await ServiceFactory.getService(ServiceNames.UserService);
      await userService.registerUser(request);
      showToast(t('registerpage:messages.registrationsuccessful'), 'success');
      navigate('/registered');
    } catch (error) {
      showToast(error.message || t('error:wrong'), 'error');
    }
  };

  return (
    <Container className="register-container" fluid>
      <Row className="register-row">
        <Col sm={2} />
        <Col>
          <Card className="form-container">
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {/* Ordinamento TAB da sinistra a destra */}
                <Row className="tab-order-row">
                  <Col sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('caption.name')}</Form.Label>
                      <Form.Control
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        ref={nameInputRef}
                        placeholder={t('registerpage:caption.name')}
                        tabIndex={1}
                      />
                      {errors.nome && <div className="error-message">{errors.nome}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>{t('registerpage:caption.username')}</Form.Label>
                      <Form.Control
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        placeholder={t('registerpage:caption.username')}
                        tabIndex={3}
                      />
                      {errors.username && (
                        <div className="error-message">{errors.username}</div>
                      )}
                    </Form.Group>

                    <Form.Group
                      className="mb-3"
                      onFocus={() => setFocusPassword(true)}
                      onBlur={() => setFocusPassword(false)}
                    >
                      <Form.Label>{t('registerpage:caption.choosePassword')}</Form.Label>
                      <div className="input-group">
                        <Form.Control
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder={t('registerpage:caption.choosePassword')}
                          tabIndex={5}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => togglePasswordVisibility('password')}
                          tabIndex={-1}
                        >
                          <i className={`bi bi-eye${showPassword ? '-slash' : ''}`} />
                        </Button>
                      </div>

                      {focusPassword && (
                        <>
                          <div className="password-strength-bar">
                            <div
                              className={`strength-level level-${passwordStrength.score}`}
                            ></div>
                          </div>
                          <ul className="password-requirements">
                            <li className={passwordStrength.checks.length ? 'valid' : ''}>
                              {passwordStrength.checks.length ? '✅' : '❌'} Minimo 8 caratteri
                            </li>
                            <li className={passwordStrength.checks.uppercase ? 'valid' : ''}>
                              {passwordStrength.checks.uppercase ? '✅' : '❌'} Una maiuscola
                            </li>
                            <li className={passwordStrength.checks.lowercase ? 'valid' : ''}>
                              {passwordStrength.checks.lowercase ? '✅' : '❌'} Una minuscola
                            </li>
                            <li className={passwordStrength.checks.number ? 'valid' : ''}>
                              {passwordStrength.checks.number ? '✅' : '❌'} Un numero
                            </li>
                            <li className={passwordStrength.checks.special ? 'valid' : ''}>
                              {passwordStrength.checks.special ? '✅' : '❌'} Un carattere
                              speciale (@ $ ! % * ? & .)
                            </li>
                          </ul>
                        </>
                      )}

                      {errors.password && (
                        <div className="error-message">{errors.password}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('registerpage:caption.surname')}</Form.Label>
                      <Form.Control
                        type="text"
                        name="cognome"
                        value={formData.cognome}
                        onChange={handleChange}
                        placeholder={t('registerpage:caption.surname')}
                        tabIndex={2}
                      />
                      {errors.cognome && (
                        <div className="error-message">{errors.cognome}</div>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>{t('registerpage:caption.email')}</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('registerpage:caption.email')}
                        tabIndex={4}
                      />
                      {errors.email && (
                        <div className="error-message">{errors.email}</div>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>{t('registerpage:caption.confirmPassword')}</Form.Label>
                      <div className="input-group">
                        <Form.Control
                          type={showNewPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder={t('registerpage:caption.confirmPassword')}
                          tabIndex={6}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => togglePasswordVisibility('newPassword')}
                          tabIndex={-1}
                        >
                          <i className={`bi bi-eye${showNewPassword ? '-slash' : ''}`} />
                        </Button>
                      </div>
                      {errors.confirmPassword && (
                        <div className="error-message">{errors.confirmPassword}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* PROFILO */}
                <Form.Group className="mb-4">
                  <Form.Label>{t('registerpage:caption.profile')}</Form.Label>
                  <Row>
                    <Col sm={4}>
                      <Form.Control
                        as="select"
                        name="profilo"
                        value={formData.profilo}
                        onChange={handleChange}
                        //disabled
                        className="custom-select"
                        tabIndex={7}
                      >
                        <option value="">
                          {t('registerpage:select.selectProfile')}
                        </option>
                        <option value="user">User</option>
                        <option value="expert">Expert</option>
                        <option value="machine">Machine</option>
                      </Form.Control>
                      {errors.profilo && (
                        <div className="error-message">{errors.profilo}</div>
                      )}
                    </Col>
                    <Col sm={8}>
                      <div className="profile-description">
                        {formData.profilo &&
                          t(`registerpage:${formData.profilo}profiledescription`)}
                      </div>
                    </Col>
                  </Row>
                </Form.Group>

                {/* TERMINI, PRIVACY, CAPTCHA */}
                <Row>
                  <Col sm={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="termsAccepted"
                        tabIndex={8}
                        label={
                          <span>
                            {t('registerpage:caption.acceptterms')}{' '}
                            <a href="#" className="link-terms">
                              Healthyland disclaimer
                            </a>
                          </span>
                        }
                        checked={formData.termsAccepted}
                        onChange={handleChange}
                      />
                      {errors.termsAccepted && (
                        <div className="error-message">{errors.termsAccepted}</div>
                      )}

                      <Form.Check
                        type="checkbox"
                        name="privacyAccepted"
                        tabIndex={9}
                        label={
                          <span>
                            {t('registerpage:caption.agreeprivacy')}{' '}
                            <a href="#" className="link-terms">
                              {t('registerpage:caption.privacypolicylink')}
                            </a>
                          </span>
                        }
                        checked={formData.privacyAccepted}
                        onChange={handleChange}
                      />
                      {errors.privacyAccepted && (
                        <div className="error-message">{errors.privacyAccepted}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col sm={6}>
                    <ReCAPTCHA
                      key={captchaKey}
                      sitekey={appConfig.CAPTCHA_KEY}
                      onChange={handleCaptchaChange}
                      hl={i18n.language}
                      tabIndex={10}
                    />
                    {errors.captcha && (
                      <div className="error-message">{errors.captcha}</div>
                    )}
                  </Col>
                </Row>

                {/* BOTTONI */}
                <div className="text-center mt-4">
                  <ButtonToolbar className="d-flex justify-content-center gap-3">
                    <Button
                      variant="primary"
                      type="submit"
                      className="register-button"
                      tabIndex={11}
                    >
                      {t('registerpage:button.caption.register')}
                    </Button>
                    <Button
                      variant="secondary"
                      className="cancel-button"
                      onClick={() => window.history.back()}
                      tabIndex={12}
                    >
                      {t('registerpage:button.caption.cancel')}
                    </Button>
                  </ButtonToolbar>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={2} />
      </Row>
    </Container>
  );
};

export default RegisterPage;